/**
 * Login page.
 *
 * Phase 17: client-side field validation + friendly API error mapping.
 * Design is unchanged from Phase 15 (branded auth card).
 */

import { useEffect, useState, FormEvent } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import { ApiError } from '../lib/api-client';
import { usePageMeta } from '../lib/page-meta';
import AuthAside from '../components/layout/AuthAside';
import Button from '../components/ui/Button';

// -------------------------------------------------------------------
// Field-error helpers
// -------------------------------------------------------------------

interface FieldErrors {
  email: string | null;
  password: string | null;
}

function emptyErrors(): FieldErrors {
  return { email: null, password: null };
}

/** Run client-side validation. Returns null if all fields pass. */
function validate(
  email: string,
  password: string,
  t: (key: string) => string,
): FieldErrors | null {
  const errs = emptyErrors();
  let hasError = false;

  if (!email.trim()) {
    errs.email = t('login.errors.emailRequired');
    hasError = true;
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
    errs.email = t('login.errors.emailInvalid');
    hasError = true;
  }

  if (!password) {
    errs.password = t('login.errors.passwordRequired');
    hasError = true;
  }

  return hasError ? errs : null;
}

/**
 * Map an ApiError into a user-facing message (and optionally a field target).
 * Returns { form, field? } where field is a key of FieldErrors when the error
 * can be attributed to a specific input.
 */
function mapApiError(err: ApiError, t: (key: string) => string): {
  form: string | null;
  field?: keyof FieldErrors;
} {
  const msg = err.message ?? '';
  const lower = msg.toLowerCase();
  const code = (err.code ?? '').toLowerCase();

  // Validation failures from the backend — show a generic form message
  // instead of the raw "Request validation failed" text.
  if (
    code === 'validation_failed' ||
    code === 'request_validation_failed' ||
    (err.statusCode === 400 && lower.includes('validation'))
  ) {
    if (lower.includes('email')) {
      return { form: null, field: 'email' };
    }
    if (lower.includes('password')) {
      return { form: null, field: 'password' };
    }
    return { form: t('login.errors.formValidation') };
  }

  // Wrong credentials
  if (err.statusCode === 401) {
    return { form: t('login.errors.incorrectCredentials') };
  }

  if (err.statusCode === 403) {
    if (code === 'email_verification_required') {
      return { form: err.message };
    }
    return { form: err.message || t('login.errors.noWorkspaceAccess') };
  }

  // Rate-limited (friendly message is already in err.message)
  if (err.statusCode === 429) {
    return { form: msg };
  }

  // Fallback: show the raw server message (it's usually already friendly)
  return { form: msg || t('login.errors.unexpected') };
}

export default function LoginPage() {
  const { t } = useTranslation('auth');
  const { login, isLoading, hasPendingWorkspaceSelection, hasPendingVerification, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  usePageMeta({
    title: t('login.metaTitle'),
    description: t('login.metaDescription'),
    canonicalPath: '/login',
    robots: 'noindex,nofollow',
  });

  const [email, setEmail] = useState(searchParams.get('email') ?? '');
  const [password, setPassword] = useState('');

  // Form-level error (banner at top of form)
  const [formError, setFormError] = useState<string | null>(null);
  // Field-level errors (shown beneath each input)
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>(emptyErrors());

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard', { replace: true });
      return;
    }

    if (hasPendingVerification) {
      navigate('/verify-email', { replace: true });
      return;
    }

    if (hasPendingWorkspaceSelection) {
      navigate('/select-workspace', { replace: true });
    }
  }, [hasPendingVerification, hasPendingWorkspaceSelection, isAuthenticated, navigate]);

  const clearFieldError = (field: keyof FieldErrors) => {
    setFieldErrors((prev) => ({ ...prev, [field]: null }));
    setFormError(null);
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setFormError(null);
    setFieldErrors(emptyErrors());

    // 1. Client-side validation
    const clientErrors = validate(email, password, t);
    if (clientErrors) {
      setFieldErrors(clientErrors);
      return;
    }

    // 2. API call
    try {
      const inviteToken = searchParams.get('inviteToken');
      const nextPath = inviteToken ? `/accept-invite/${inviteToken}` : '/dashboard';
      const result = await login(email.trim(), password, nextPath);
      navigate(
        result.status === 'authenticated'
          ? nextPath
          : result.status === 'workspace_selection_required'
            ? '/select-workspace'
            : '/verify-email',
      );
    } catch (err) {
      if (err instanceof ApiError) {
        const { form, field } = mapApiError(err, t);
        if (field) {
          const errorMsg = field === 'email'
            ? t('login.errors.emailInvalid')
            : t('login.errors.genericField');
          setFieldErrors((prev) => ({ ...prev, [field]: errorMsg }));
        } else {
          setFormError(form);
        }
      } else if (err instanceof TypeError) {
        console.error('[LoginPage] Network error:', err);
        setFormError(t('login.errors.network'));
      } else {
        console.error('[LoginPage] Unexpected error during login:', err);
        setFormError(t('login.errors.unexpected'));
      }
    }
  };

  return (
    <div className="acc-auth-page">
      <div className="acc-auth-shell">
        <AuthAside />

        <div className="acc-auth-card acc-auth-card--panel">
          <div className="acc-auth-panel__header">
            <div className="acc-auth-panel__eyebrow">{t('login.title')}</div>
            <h1 className="acc-auth-card__title">{t('login.title')}</h1>
            <p className="acc-auth-card__description">
              {t('login.description')}
            </p>
          </div>

          {formError && (
            <div role="alert" className="acc-auth-banner acc-auth-banner--danger">
              {formError}
            </div>
          )}

          <form onSubmit={handleSubmit} noValidate className="acc-auth-form">
            <div className="acc-auth-form__grid">
            <div className="acc-auth-form__group acc-auth-form__group--span-2">
              <label htmlFor="email" className="acc-auth-form__label">{t('login.emailLabel')}</label>
              <input
                id="email"
                type="email"
                className={`acc-input${fieldErrors.email ? ' acc-input--error' : ''}`}
                value={email}
                onChange={(e) => { setEmail(e.target.value); clearFieldError('email'); }}
                autoComplete="email"
                placeholder={t('login.emailPlaceholder')}
                aria-invalid={!!fieldErrors.email}
                aria-describedby={fieldErrors.email ? 'email-error' : undefined}
              />
              {fieldErrors.email && (
                <span id="email-error" className="acc-auth-form__error" role="alert">
                  {fieldErrors.email}
                </span>
              )}
            </div>

            <div className="acc-auth-form__group acc-auth-form__group--span-2">
              <label htmlFor="password" className="acc-auth-form__label">{t('login.passwordLabel')}</label>
              <input
                id="password"
                type="password"
                className={`acc-input${fieldErrors.password ? ' acc-input--error' : ''}`}
                value={password}
                onChange={(e) => { setPassword(e.target.value); clearFieldError('password'); }}
                autoComplete="current-password"
                placeholder={t('login.passwordPlaceholder')}
                aria-invalid={!!fieldErrors.password}
                aria-describedby={fieldErrors.password ? 'password-error' : undefined}
              />
              {fieldErrors.password ? (
                <span id="password-error" className="acc-auth-form__error" role="alert">
                  {fieldErrors.password}
                </span>
              ) : (
                <span className="acc-auth-form__hint acc-auth-form__hint--placeholder" aria-hidden="true">
                  {t('login.passwordHint')}
                </span>
              )}
              <div className="acc-auth-form__hint acc-auth-form__hint--note">
                {t('login.workspaceResolutionHint')}
              </div>
            </div>

            <div className="acc-auth-form__utility-row acc-auth-form__utility-row--span-2">
              <Link
                to={email.trim() ? `/forgot-password?email=${encodeURIComponent(email.trim())}` : '/forgot-password'}
                className="acc-link-inline"
              >
                {t('login.forgotPassword')}
              </Link>
            </div>

            <div className="acc-auth-form__actions acc-auth-form__actions--span-2">
              <Button
                type="submit"
                disabled={isLoading}
                size="lg"
                style={{ width: '100%' }}
              >
                {isLoading ? t('login.submitting') : t('login.submit')}
              </Button>
            </div>
            </div>
          </form>

          <p className="acc-auth-footer acc-auth-footer--panel">
            {t('login.footer')}{' '}
            <Link to="/register" className="acc-link-inline">
              {t('login.footerAction')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
